import { Money } from "@shop-ledger/shared-types";
import { getSalesExpenseSummary } from "../api/reportsApi";
import { useAuth } from "../auth/AuthContext";
import { useAsyncData } from "../lib/useAsyncData";
import { StatusBanner } from "./StatusBanner";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Same stand-in DashboardPage/TillPage use — see
// DashboardPage.tsx's own note on why (no tenant-settings endpoint exposes `Tenant.currency`).

/**
 * Dashboard "sales & expenses by period" row — eight fixed at-a-glance cards (today through
 * last year) from `GET /reports/sales-expense-summary`. Each card's expense figure is
 * prorated for routine (recurring) expenses rather than a raw same-day sum — a monthly rent
 * payment logged on the 1st shows up as its daily share in "today", not the whole amount —
 * so "today"/"this week" read honestly instead of spiking on whatever day rent was logged.
 * See `reports_service.sales_expense_summary`'s docstring for the full proration rule.
 */
export function PeriodSummaryCards() {
  const { ensureFreshToken } = useAuth();
  const state = useAsyncData(() => ensureFreshToken().then((token) => getSalesExpenseSummary(token)), []);

  if (state.error) return <StatusBanner kind="error">{state.error}</StatusBanner>;

  return (
    <div className="panel">
      <h2 className="panel__title">Sales &amp; expenses by period</h2>
      {state.loading || !state.data ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="sl-period-grid">
          {state.data.periods.map((period) => {
            const isNegative = Money.isNegative(period.net_total);
            return (
              <div key={period.key} className="sl-period-card">
                <p className="sl-period-card__label">{period.label}</p>
                <dl className="sl-period-card__rows">
                  <div className="sl-period-card__row">
                    <dt>Sales</dt>
                    <dd>{Money.format(period.sales_total, TENANT_CURRENCY_FALLBACK)}</dd>
                  </div>
                  <div className="sl-period-card__row">
                    <dt>Expenses</dt>
                    <dd>{Money.format(period.expense_total, TENANT_CURRENCY_FALLBACK)}</dd>
                  </div>
                  <div className="sl-period-card__row sl-period-card__row--net">
                    <dt>Net</dt>
                    <dd className={isNegative ? "sl-period-card__net--negative" : "sl-period-card__net--positive"}>
                      {Money.format(period.net_total, TENANT_CURRENCY_FALLBACK)}
                    </dd>
                  </div>
                </dl>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
